import { Link } from "react-router-dom";
import { ActionIcon, Anchor, Badge, Button, Card, Group, Paper, Select, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { BookingCostFilters, BookingCostRow, BookingCosts } from "@/lib/api/billing";
import { clock, dayClock } from "@/lib/clock";
import { REASONS, REASON_LABEL, invoiceText, isFee, kr, monthLabel, monthParam, reasonText, shiftMonth } from "@/lib/booking-costs";

export interface BookingCostsViewProps {
  data: BookingCosts | undefined;
  isLoading: boolean;
  /** HTTP status of a failed load (403 = not a company admin), or true for any other failure. */
  error: number | true | null;
  /** "YYYY-MM" */
  month: string;
  onMonthChange: (month: string) => void;
  /** The company view (tenant admins): "Booked by" and "Invoice" columns and the filters. */
  company: boolean;
  filters?: Omit<BookingCostFilters, "month">;
  onFiltersChange?: (filters: Omit<BookingCostFilters, "month">) => void;
  /** Company view: open the PDF of the invoice a row is on (spec §8). */
  onInvoicePdf?: (invoice: NonNullable<BookingCostRow["invoice"]>) => void;
  /** The invoice whose PDF is downloading (one at a time). */
  downloadingInvoiceId?: number | null;
}

/** The Invoice cell: the PDF when there is one, else the Invoices tab that lists the invoice, else plain text. */
function InvoiceCell({
  row,
  onInvoicePdf,
  downloadingInvoiceId,
}: {
  row: BookingCostRow;
  onInvoicePdf?: BookingCostsViewProps["onInvoicePdf"];
  downloadingInvoiceId?: number | null;
}) {
  const invoice = row.invoice;
  if (!invoice || row.invoice_state === "credited") return <>{invoiceText(row)}</>;
  if (invoice.has_pdf && onInvoicePdf) {
    return (
      <Anchor
        component="button"
        type="button"
        fz="sm"
        data-testid={`invoice-pdf-${invoice.id}`}
        disabled={downloadingInvoiceId != null}
        onClick={() => onInvoicePdf(invoice)}
      >
        {downloadingInvoiceId === invoice.id ? "Downloading…" : invoiceText(row)}
      </Anchor>
    );
  }
  return (
    <Anchor component={Link} to="/invoices" fz="sm">
      {invoiceText(row)}
    </Anchor>
  );
}

/** Booking costs — presentational, so it renders without the API client. */
export function BookingCostsView({
  data,
  isLoading,
  error,
  month,
  onMonthChange,
  company,
  filters,
  onFiltersChange,
  onInvoicePdf,
  downloadingInvoiceId,
}: BookingCostsViewProps) {
  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Group gap="xs">
          <ActionIcon variant="subtle" aria-label="Previous month" onClick={() => onMonthChange(shiftMonth(month, -1))}>
            <ChevronLeft size={16} />
          </ActionIcon>
          <Text fw={600} miw={90} ta="center">{monthLabel(month)}</Text>
          <ActionIcon
            variant="subtle"
            aria-label="Next month"
            disabled={month >= monthParam()}
            onClick={() => onMonthChange(shiftMonth(month, 1))}
          >
            <ChevronRight size={16} />
          </ActionIcon>
        </Group>
        {company && filters && onFiltersChange && data && (
          <Group gap="xs" wrap="wrap">
            <Select
              size="xs"
              placeholder="Everyone"
              clearable
              data={data.members.map((m) => ({ value: String(m.id), label: m.name }))}
              value={filters.member ? String(filters.member) : null}
              onChange={(v) => onFiltersChange({ ...filters, member: v ? Number(v) : null })}
            />
            <Select
              size="xs"
              placeholder="All rooms"
              clearable
              data={data.rooms.map((r) => ({ value: String(r.id), label: r.name }))}
              value={filters.room ? String(filters.room) : null}
              onChange={(v) => onFiltersChange({ ...filters, room: v ? Number(v) : null })}
            />
            <Select
              size="xs"
              placeholder="All reasons"
              clearable
              data={REASONS.map((r) => ({ value: r, label: REASON_LABEL[r] }))}
              value={filters.reason}
              onChange={(v) => onFiltersChange({ ...filters, reason: (v as typeof filters.reason) ?? null })}
            />
          </Group>
        )}
      </Group>
      <BookingCostsBody
        data={data}
        isLoading={isLoading}
        error={error}
        month={month}
        onMonthChange={onMonthChange}
        company={company}
        onInvoicePdf={onInvoicePdf}
        downloadingInvoiceId={downloadingInvoiceId}
      />
    </Stack>
  );
}

function BookingCostsBody({
  data,
  isLoading,
  error,
  month,
  onMonthChange,
  company,
  onInvoicePdf,
  downloadingInvoiceId,
}: BookingCostsViewProps) {
  if (isLoading) return <Text fz="sm">Loading booking costs…</Text>;
  if (error === 403) return <Text fz="sm" c="dimmed">Only your company&apos;s admins can see booking costs.</Text>;
  if (error || !data) return <Text fz="sm" c="red">Booking costs could not be loaded. Please try again later.</Text>;
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
        <Paper withBorder p="sm">
          <Text fz="xs" c="dimmed">{`Total ${monthLabel(month)}`}</Text>
          <Text fw={700} fz="lg">{kr(data.totals.total)}</Text>
        </Paper>
        <Paper withBorder p="sm">
          <Text fz="xs" c="dimmed">of which no-show fees</Text>
          <Text fw={700} fz="lg" c="orange">{kr(data.totals.no_show_fees)}</Text>
        </Paper>
        <Paper withBorder p="sm">
          <Text fz="xs" c="dimmed">of which late cancellations</Text>
          <Text fw={700} fz="lg" c="orange">{kr(data.totals.late_cancellations)}</Text>
        </Paper>
      </SimpleGrid>
      {company && (
        <Text fz="xs" c="dimmed">
          The totals above follow your filters; the months below are the company&apos;s totals, unfiltered.
        </Text>
      )}
      <Group gap={4} wrap="wrap">
        {data.months.map((m) => (
          <Button
            key={m.month}
            size="compact-xs"
            variant={m.month.startsWith(month) ? "filled" : "light"}
            onClick={() => onMonthChange(m.month.slice(0, 7))}
          >
            {`${monthLabel(m.month)} · ${kr(m.total)}`}
          </Button>
        ))}
      </Group>
      {data.rows.length === 0 ? (
        <Card withBorder padding="lg">
          <Text fz="sm" c="dimmed">{`No charged bookings in ${monthLabel(month)}.`}</Text>
        </Card>
      ) : (
        <Card withBorder padding={0}>
          <Table.ScrollContainer minWidth={company ? 920 : 640}>
            <Table verticalSpacing="sm" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>When</Table.Th>
                  <Table.Th>Room</Table.Th>
                  {company && <Table.Th>Booked by</Table.Th>}
                  <Table.Th>Reason</Table.Th>
                  <Table.Th ta="right">Amount</Table.Th>
                  <Table.Th>Reference</Table.Th>
                  {company && <Table.Th>Invoice</Table.Th>}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.rows.map((row) => (
                  <Table.Tr key={row.charge_id} opacity={row.active ? 1 : 0.6}>
                    <Table.Td>{`${dayClock(row.start, row.site_time_zone)}–${clock(row.end, row.site_time_zone)} (${Number(row.hours)} h)`}</Table.Td>
                    <Table.Td>{row.room_name}</Table.Td>
                    {company && <Table.Td>{row.booked_by_name ?? ""}</Table.Td>}
                    <Table.Td>
                      {isFee(row.reason) ? (
                        <Badge color="orange" variant="light" tt="none">{reasonText(row)}</Badge>
                      ) : (
                        reasonText(row)
                      )}
                    </Table.Td>
                    <Table.Td ta="right">{kr(row.amount)}</Table.Td>
                    <Table.Td>{row.reference}</Table.Td>
                    {company && (
                      <Table.Td>
                        <InvoiceCell row={row} onInvoicePdf={onInvoicePdf} downloadingInvoiceId={downloadingInvoiceId} />
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
    </Stack>
  );
}
